import type { ReactElement } from "react";
import { useId } from "react";

import { FormFieldLabel } from "@core/components/FormFieldLabel";
import { SupportingText } from "@core/components/SupportingText";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import type { SelectionTileItem, SelectionTileItemProps } from "./components/SelectionTileItem";

import { SelectionTileGroupContext, type SelectionTileType } from "./SelectionTileGroupContext";

import styles from "./SelectionTileGroup.module.scss";

type SelectionTileItemChild = ReactElement<SelectionTileItemProps, typeof SelectionTileItem> | false | null | undefined;

export type SelectionTileGroupProps = {
  name?: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  isError?: boolean;
  orientation?: "vertical" | "horizontal";
  type?: SelectionTileType;
  variant?: "default" | "content";
  children: SelectionTileItemChild | SelectionTileItemChild[];
} & SafeHtmlAttributes<HTMLFieldSetElement>;

export const SelectionTileGroup = ({
  name,
  label,
  helperText,
  errorText,
  isError = false,
  orientation = "vertical",
  type = "radio",
  variant = "default",
  children,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: SelectionTileGroupProps) => {
  const supportingTextId = useId();
  const labelId = useId();
  const showHelperText = !isError && helperText;
  const showErrorText = isError && errorText;
  const resolvedAriaDescribedBy =
    [ariaDescribedBy, showHelperText || showErrorText ? supportingTextId : undefined]
      .flatMap((value) => value?.split(/\s+/) ?? [])
      .filter(Boolean)
      .join(" ") || undefined;
  const listClassName = classnames(styles.list, styles[orientation], {
    [styles.content]: orientation === "horizontal" && variant === "content",
  });

  return (
    <fieldset
      {...rest}
      className={styles.group}
      role={type === "radio" ? "radiogroup" : undefined}
      aria-labelledby={label ? labelId : undefined}
      aria-describedby={resolvedAriaDescribedBy}
      aria-invalid={isError || undefined}
    >
      {label && (
        <legend id={labelId} className={styles.legend}>
          <FormFieldLabel label={label} />
        </legend>
      )}

      <SelectionTileGroupContext.Provider value={{ isError, groupName: name, groupType: type }}>
        <div className={listClassName}>{children}</div>
      </SelectionTileGroupContext.Provider>

      {showHelperText && (
        <SupportingText id={supportingTextId} variant="info">
          {helperText}
        </SupportingText>
      )}

      {showErrorText && (
        <SupportingText id={supportingTextId} variant="error" role="alert">
          {errorText}
        </SupportingText>
      )}
    </fieldset>
  );
};
